import React, { useState, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  addMonths,
  subMonths,
} from "date-fns";
import { fr } from "date-fns/locale";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  useMediaQuery as muiUseMediaQuery,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import FlipClock from "./FlipClock";

const ClockCalendar = () => {
  const theme = useTheme();
  const isSmall = muiUseMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = muiUseMediaQuery(theme.breakpoints.down("md"));

  const [time, setTime] = useState(new Date());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handlePrevMonth = () => {
    setDirection(-1);
    setCurrentDate((prev) => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setDirection(1);
    setCurrentDate((prev) => addMonths(prev, 1));
  };

  const getTimeInZone = (utcOffset) => {
    const utcTime = new Date(time.getTime() + time.getTimezoneOffset() * 60000);
    return format(new Date(utcTime.getTime() + utcOffset * 3600000), "HH:mm");
  };

  const daysOfWeek = ["L", "M", "M", "J", "V", "S", "D"];
  const currentMonth = format(currentDate, "MMMM yyyy", { locale: fr }).toUpperCase();
  const today = format(new Date(), "yyyy-MM-dd");
  const monthDays = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate),
  });

  return (
    <Paper
      elevation={5}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: isSmall ? 2 : 3,
        borderRadius: "12px",
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.15)",
        width: "100%",
        maxWidth: "1000px",
        margin: "auto",
      }}
    >
      {/* Partie Heure + Calendrier */}
      <Box
        display="flex"
        flexDirection={isMedium ? "column" : "row"}
        alignItems="center"
        justifyContent="space-evenly"
        width="100%"
        gap={isMedium ? 3 : 0}
      >
        {/* Heure locale */}
        <Box textAlign="center" flex="1">
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              fontSize: isSmall ? "3.5rem" : isMedium ? "5rem" : "7.5rem",
              color: "#21295c",
              lineHeight: 1.1,
            }}
          >
            {format(time, "HH:mm")}
          </Typography>
          <Typography
            variant="h6"
            textTransform="uppercase"
            sx={{ fontSize: isSmall ? "1rem" : "1.4rem" }}
          >
            {format(time, "EEEE d MMMM", { locale: fr })}
          </Typography>
        </Box>

        {/* Séparateur */}
        {!isMedium && (
          <Divider orientation="vertical" flexItem sx={{ mx: 3 }} />
        )}

        {/* Calendrier */}
        <Box flex="1" textAlign="center">
          <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
            <IconButton onClick={handlePrevMonth} size="small">
              <ChevronLeft sx={{ color: "#21295c" }} />
            </IconButton>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentMonth}
                initial={{ opacity: 0, x: direction === 1 ? 10 : -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction === 1 ? -10 : 10 }}
                transition={{ duration: 0.3 }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {currentMonth}
                </Typography>
              </motion.div>
            </AnimatePresence>
            <IconButton onClick={handleNextMonth} size="small">
              <ChevronRight sx={{ color: "#21295c" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              backgroundColor: theme.palette.background.default,
              boxShadow: "inset 0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5}>
              {daysOfWeek.map((day, index) => (
                <Typography key={index} variant="caption" fontWeight="bold" textAlign="center">
                  {day}
                </Typography>
              ))}

              {Array(Math.max(0, getDay(monthDays[0]) - 1))
                .fill("")
                .map((_, index) => (
                  <Box key={`empty-${index}`} />
                ))}

              <AnimatePresence mode="wait">
                {monthDays.map((day) => {
                  const formattedDay = format(day, "d");
                  const isToday = format(day, "yyyy-MM-dd") === today;
                  return (
                    <motion.div
                      key={day}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        sx={{
                          p: 0.8,
                          textAlign: "center",
                          borderRadius: "50%",
                          fontSize: "0.85rem",
                          bgcolor: isToday ? "#FFD166" : "transparent",
                          color: isToday ? "#fff" : theme.palette.text.primary,
                          fontWeight: isToday ? "bold" : "normal",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          width: isSmall ? "26px" : "30px",
                          height: isSmall ? "26px" : "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            bgcolor: theme.palette.secondary.light,
                            color: "#fff",
                          },
                        }}
                      >
                        <Typography variant="caption">{formattedDay}</Typography>
                      </Box>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Villes internationales */}
      <Box
        display="flex"
        flexDirection={isSmall ? "column" : "row"}
        justifyContent="center"
        alignItems="center"
        gap={isSmall ? 2 : 6}
        width="100%"
        mt={3}
      >
        <FlipClock city="New York" time={getTimeInZone(-5)} />
        <FlipClock city="Dubaï" time={getTimeInZone(4)} />
        <FlipClock city="Singapour" time={getTimeInZone(8)} />
      </Box>
    </Paper>
  );
};

export default ClockCalendar;
